import type { BrickId, BrickPattern, CornerSnap, Measurement, Mm, PlacedBrick, SnapResult, WallModel } from '@bw/core';
import { Viewport } from './viewport.js';
import type { DrawTheme } from './theme.js';
import { LIGHT_THEME } from './theme.js';
import { drawGrid } from './grid.js';
import { paintEmptyHint, paintWall } from './wall-painter.js';
import type { WallPaintStats } from './wall-painter.js';
import type { GeneratePreview } from './overlay-painter.js';
import type { SketchOverlay } from './sketch-painter.js';
import { paintSketch } from './sketch-painter.js';
import { paintPlan } from './plan-painter.js';
import type { PlanScene } from './plan-painter.js';
import {
  paintBuildPreview,
  paintGeneratePreview,
  paintKeyboardCursor,
  paintMarquee,
  paintMeasurement,
  paintPastePreview,
  paintSnapGuide,
} from './overlay-painter.js';

export interface BuildPreview {
  readonly snap: SnapResult;
  readonly width: Mm;
  readonly height: Mm;
  readonly valid: boolean;
  readonly caption: string;
}

export interface PastePreview {
  readonly snap: SnapResult;
  readonly pattern: BrickPattern;
  readonly valid: boolean;
  readonly caption: string;
}

export interface MarqueeRect {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

export interface MeasureOverlay {
  readonly measurement: Measurement | null;
  readonly start: CornerSnap | null;
  readonly hover: CornerSnap | null;
}

/** Everything the renderer needs for one frame, pulled — never pushed. */
export interface Scene {
  readonly model: WallModel;
  readonly colourOf: (brick: PlacedBrick) => string;
  readonly colourOfMaterial: (materialId: string) => string;
  readonly selected: ReadonlySet<BrickId>;
  readonly dimmed?: ReadonlySet<BrickId> | undefined;
  /** False while the welcome card covers the drawing, which says the same
      thing better. Defaults to on, so the hint is never lost by omission. */
  readonly emptyHint?: boolean | undefined;
  /**
   * Present only in the plan tool. When it is, the wall layer draws the whole
   * building from above instead of one wall's elevation — a different view of
   * the same document, not a different renderer (phase 2, M5).
   */
  readonly plan?: PlanScene | undefined;
  readonly build?: BuildPreview | undefined;
  readonly paste?: PastePreview | undefined;
  readonly marquee?: MarqueeRect | undefined;
  readonly generate?: GeneratePreview | undefined;
  readonly sketch?: SketchOverlay | undefined;
  readonly keyboardCursor?: { readonly x: number; readonly y: number } | undefined;
  readonly measure?: MeasureOverlay | undefined;
}

/**
 * Two stacked canvases (design document B4).
 *
 * The wall canvas repaints only when the model revision or the viewport
 * changes. The overlay repaints every frame it is dirty, holding at most a few
 * hundred shapes. Dragging a preview across a 5,000-brick wall therefore
 * touches only the overlay.
 */
export class Renderer {
  readonly viewport = new Viewport();
  #wall: CanvasRenderingContext2D;
  #overlay: CanvasRenderingContext2D;
  #wallCanvas: HTMLCanvasElement;
  #overlayCanvas: HTMLCanvasElement;
  #theme: DrawTheme = LIGHT_THEME;
  #scene: (() => Scene) | null = null;
  #wallDirty = true;
  #overlayDirty = true;
  #frame: number | null = null;
  #dpr = 1;
  #lastStats: WallPaintStats = { visible: 0, detailed: true };
  #onFrame: ((stats: WallPaintStats) => void) | null = null;

  constructor(wallCanvas: HTMLCanvasElement, overlayCanvas: HTMLCanvasElement) {
    this.#wallCanvas = wallCanvas;
    this.#overlayCanvas = overlayCanvas;
    const wall = wallCanvas.getContext('2d', { alpha: false });
    const overlay = overlayCanvas.getContext('2d');
    if (wall === null || overlay === null) throw new Error('Canvas 2D is unavailable');
    this.#wall = wall;
    this.#overlay = overlay;
  }

  get theme(): DrawTheme {
    return this.#theme;
  }

  setTheme(theme: DrawTheme): void {
    this.#theme = theme;
    this.markWallDirty();
    this.markOverlayDirty();
  }

  setScene(provider: () => Scene): void {
    this.#scene = provider;
    this.markWallDirty();
  }

  onFrame(callback: (stats: WallPaintStats) => void): void {
    this.#onFrame = callback;
  }

  get stats(): WallPaintStats {
    return this.#lastStats;
  }

  markWallDirty(): void {
    this.#wallDirty = true;
    this.#overlayDirty = true;
  }

  markOverlayDirty(): void {
    this.#overlayDirty = true;
  }

  /** Size both canvases to the CSS box, allowing for device pixel ratio. */
  resize(cssWidth: number, cssHeight: number, devicePixelRatio = 1): void {
    const dpr = Math.max(1, devicePixelRatio);
    const changed = this.viewport.resize(cssWidth, cssHeight) || dpr !== this.#dpr;
    this.#dpr = dpr;
    for (const [canvas, ctx] of [
      [this.#wallCanvas, this.#wall],
      [this.#overlayCanvas, this.#overlay],
    ] as const) {
      const w = Math.max(1, Math.round(cssWidth * dpr));
      const h = Math.max(1, Math.round(cssHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    if (changed) this.markWallDirty();
  }

  start(): void {
    if (this.#frame !== null) return;
    const tick = (): void => {
      this.#frame = requestAnimationFrame(tick);
      this.render();
    };
    this.#frame = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  /** Paint whatever is dirty. Safe to call directly in tests. */
  render(): void {
    const provider = this.#scene;
    if (provider === null) return;
    if (!this.#wallDirty && !this.#overlayDirty) return;
    const scene = provider();

    if (this.#wallDirty) {
      const ctx = this.#wall;
      ctx.save();
      ctx.fillStyle = this.#theme.background;
      ctx.fillRect(0, 0, this.viewport.width, this.viewport.height);
      ctx.restore();
      drawGrid(ctx, this.viewport, this.#theme);
      if (scene.plan !== undefined) {
        paintPlan(ctx, this.viewport, this.#theme, scene.plan);
      } else if (scene.model.size === 0) {
        if (scene.emptyHint !== false) paintEmptyHint(ctx, this.viewport, this.#theme);
      } else {
        this.#lastStats = paintWall(ctx, {
          model: scene.model,
          viewport: this.viewport,
          theme: this.#theme,
          colourOf: scene.colourOf,
          selected: scene.selected,
          dimmed: scene.dimmed,
        });
      }
      this.#wallDirty = false;
      this.#onFrame?.(this.#lastStats);
    }

    if (this.#overlayDirty) {
      const ctx = this.#overlay;
      ctx.clearRect(0, 0, this.viewport.width, this.viewport.height);
      if (scene.build !== undefined) {
        paintSnapGuide(
          ctx,
          this.viewport,
          this.#theme,
          scene.model,
          scene.build.snap,
          scene.build.width,
          scene.build.height,
        );
        paintBuildPreview(
          ctx,
          this.viewport,
          this.#theme,
          scene.build.snap,
          scene.build.width,
          scene.build.height,
          scene.build.valid,
          scene.build.caption,
        );
      }
      if (scene.paste !== undefined) {
        const relation = scene.paste.snap.relation;
        // Joint guides would be ambiguous for a group (clause 12.4).
        if (relation === 'baseline' || relation === 'y-axis' || relation === 'axes') {
          paintSnapGuide(ctx, this.viewport, this.#theme, scene.model, scene.paste.snap, 0, 0);
        }
        paintPastePreview(
          ctx,
          this.viewport,
          this.#theme,
          scene.paste.snap,
          scene.paste.pattern,
          scene.paste.valid,
          scene.paste.caption,
          scene.colourOfMaterial,
        );
      }
      // The sketch sits under the transient previews but over the wall.
      if (scene.sketch !== undefined) {
        paintSketch(ctx, this.viewport, this.#theme, scene.sketch);
      }
      if (scene.generate !== undefined) {
        paintGeneratePreview(ctx, this.viewport, this.#theme, scene.generate);
      }
      if (scene.marquee !== undefined) {
        paintMarquee(
          ctx,
          this.#theme,
          scene.marquee.x1,
          scene.marquee.y1,
          scene.marquee.x2,
          scene.marquee.y2,
        );
      }
      if (scene.keyboardCursor !== undefined) {
        paintKeyboardCursor(ctx, this.viewport, this.#theme, scene.keyboardCursor);
      }
      if (scene.measure !== undefined) {
        paintMeasurement(
          ctx,
          this.viewport,
          this.#theme,
          scene.measure.measurement,
          scene.measure.start,
          scene.measure.hover,
        );
      }
      this.#overlayDirty = false;
    }
  }
}
